/**
 * Pure-function resolver + chip→bucket mapper used by
 * `useMultiAddBucketNotifications`.
 *
 * Extracted to its own module so the resolver tests can import the
 * symbols without dragging in React, the depot, or the agent broker —
 * the framework-agnostic decision points live in a separate seam from
 * the React adapter (`use-multi-add-bucket-notifications.ts`).
 *
 * Per `think-in-systems` (ai.knowledge/global): minimum coupling —
 * the resolver depends only on type imports plus the dependency-free
 * `device/setup-order.ts` (no runtime React, no depot, no broker).
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
import type { Agent } from '@studio/models/agent';
import type { Model } from '@studio/models/device';
import type { ChipState, ChipTargetTab } from '#~/room/components/video/sources/item/chip-state.js';
import type { MultiAddSkipReason } from '#~/notifications/types.js';
import type { MultiAddOrigin } from '#~/depot/types.js';
import type { T_Maybe } from '@tryiris-ai/js-types';
// Dependency-free shared ordering — keeps the resolver free of React / depot /
// broker runtime coupling while routing exactly like the tile chip.
import { firstInSetupOrder } from '#~/device/setup-order.js';

/**
 * Subset of the studio Device shape the chip cascade needs. Mirrors
 * `useChipState`'s read contract so the lookup can be wired through
 * `deriveChipState` with no type drift.
 *
 * Per `think-in-systems` (ai.knowledge/global): minimal interface — the
 * lookup only owes the hook these two fields; the broader Device shape
 * stays decoupled.
 */
export interface OriginDeviceSnapshot {
  /** Display name pinned at notification post time (does not re-render on rename). */
  deviceName: string;
  /** Agent status — gates bridge-offline / offline-embedded branches in `deriveChipState`. */
  agentStatus?: T_Maybe<Agent['status']>;
  /** Model id — gates the "Select model" branch. */
  modelId?: T_Maybe<Model['id']>;
}

/**
 * Lookup function the resolver calls per origin device. Returns:
 *   - `chipState` — the single top-priority cascade verdict. Supplies the
 *     inline verb for a SPECIFIC fire (chip verbs unchanged, §2.7).
 *   - `missing` — the FULL set of factually-missing buckets from
 *     `deriveMissingBuckets` (ENG-4486 §2.1). This, not `chipState`, decides
 *     single-vs-combined: `missing.length` is the grouping signal (§2.2).
 *   - `deviceSnapshot` — the display-name snapshot pinned at post time.
 * Returns `undefined` when the device is not yet known to any room slicer
 * (broker-race window — treated as transient and skipped).
 */
export type LookupOriginChipFn = (deviceId: string) =>
  | {
      chipState: ChipState | null;
      missing: MultiAddSkipReason[];
      deviceSnapshot: OriginDeviceSnapshot;
    }
  | undefined;

/**
 * Bucket key derived from `ChipState.targetTab`. Returns the canonical
 * `MultiAddSkipReason` string that the observer hook's `post()` carries +
 * `skipReasonToDefaultTab` routes to the matching Iris-Connection
 * sub-tab.
 *
 * Mapping:
 *   - `authentication` → `auth`
 *   - `video`          → `video-failed`
 *   - `control`        → `control-failed`
 *   - `driver`         → `missing-model`
 *
 * Per `think-in-systems` (ai.knowledge/global): single-source-of-truth
 * mapper — every consumer that needs the chip→bucket translation
 * routes through this helper.
 */
export const chipTargetTabToBucket = (targetTab: ChipTargetTab): MultiAddSkipReason => {
  switch (targetTab) {
    case 'authentication':
      return 'auth';
    case 'video':
      // `skipReasonToDefaultTab` routes `video-failed` (an existing
      // MultiAddSkipReason member) to the Video tab.
      return 'video-failed';
    case 'control':
      // `control-failed` likewise routes to Control.
      return 'control-failed';
    case 'driver':
      return 'missing-model';
  }
};

// PRODUCT-CONFIRMED combined-alert copy (ENG-4486 §11 resolved). "Additional
// configuration required" is the exact phrase product used when raising the
// issue; it pairs naturally with the existing `Authentication required` title
// and satisfies PRD §9.2 — the status indicates that several device settings
// are missing without enumerating which. `Open settings` is the neutral
// inline action verb for the combined alert (no single chip verb applies).
// Per `reference-rules-in-comments` (ai.knowledge/global).
export const COMBINED_TITLE = 'Additional configuration required';
export const COMBINED_ACTION_LINK = 'Open settings';

/**
 * A single bucket-fire instruction — the DESIRED alert for a device. The
 * hook's reconciliation turns it into a `post()` (and tracks its identity).
 *
 * `kind` + `buckets` were added for ENG-4486:
 *   - `kind: 'single'`   — exactly one missing bucket → the specific alert
 *     (unchanged behavior). `buckets === [bucket]`.
 *   - `kind: 'combined'` — two or more missing buckets → ONE combined alert
 *     for the device (PRD §9.2). `buckets` is the full ordered missing set;
 *     `bucket` is the ROUTING bucket (auth-first, §6.2).
 */
export interface BucketFire {
  /** `'single'` for one missing setting, `'combined'` for two or more. */
  kind: 'single' | 'combined';
  deviceId: string;
  /**
   * Routing bucket — drives the deep-link tab and (for single) the copy.
   * Single: the one missing bucket. Combined: the first missing bucket in
   * `SETUP_BUCKET_ORDER` (auth -> video -> driver -> control; auth-first, §6.2).
   */
  bucket: MultiAddSkipReason;
  /** Full ordered set of missing buckets (single: one element). */
  buckets: MultiAddSkipReason[];
  batchId: string;
  deviceName: string;
  /**
   * Inline call-to-action verb. Single: verbatim from `chipState.actionLink`
   * (chip verbs unchanged, §2.7). Combined: `COMBINED_ACTION_LINK`.
   */
  actionLink: string;
}

/**
 * Per-bucket toast copy for the floating `<Notice>` surfaced alongside
 * the bell-tray entry. Returns `{title, message}` for the four chip
 * cascade buckets the hook emits.
 *
 * Why this lives next to the resolver: per
 * `tests-require-user-story-comment` (ai.knowledge/global) the copy
 * itself is product-facing surface that needs locked-in test coverage
 * (a typo in the singular "Authentication required" header would brick
 * the user's mental anchor with the chip's "Authenticate" verb). Keeping
 * the helper framework-agnostic — same precedent as `chipTargetTabToBucket`
 * — makes it testable without React.
 *
 * The floating Notice is the PRIMARY surface; the bell-tray entry
 * remains the persistent queue.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
export const bucketToToastCopy = (
  bucket: MultiAddSkipReason,
  deviceName: string
): { title: string; message: string } => {
  switch (bucket) {
    case 'auth':
      return {
        title: 'Authentication required',
        message: `Your camera "${deviceName}" requires authentication to connect to Iris.`
      };
    case 'video-failed':
      // ENG-4486 §2B: renamed to the PRD §9.2 type name (was "Video setup
      // required"). Body copy unchanged. These three specific titles + the
      // combined title (see combinedToastCopy) are all product-confirmed.
      return {
        title: 'Configure Video',
        message: `Iris could not connect to the video stream for "${deviceName}".`
      };
    case 'control-failed':
      // ENG-4486 §2B: was "Controls setup required".
      return {
        title: 'Configure Control',
        message: `Iris could not connect to the controls for "${deviceName}".`
      };
    case 'missing-model':
      // ENG-4486 §2B: was "Camera model required".
      return {
        title: 'Select Model',
        message: `Camera "${deviceName}" needs a brand and model selected.`
      };
    default:
      // Defensive fallback for any future MultiAddSkipReason variants
      // that leak through. The hook today only ever emits the four
      // above (per `chipTargetTabToBucket`), but a future cascade tab
      // landing without an updated mapper should still render a sane
      // toast rather than blank fields.
      // Per `verify-and-escalate` (ai.knowledge/global): degrade
      // gracefully on unknown input rather than crashing the toast.
      return {
        title: 'Camera needs configuration',
        message: `Camera "${deviceName}" needs additional setup.`
      };
  }
};

/**
 * Copy for the COMBINED alert (ENG-4486 §2.3) fired when a device is missing
 * two or more settings (PRD §9.2). PRODUCT-CONFIRMED: the title is the exact
 * phrase product raised the issue with, and the body states that several
 * settings are missing WITHOUT enumerating them (product chose the shorter,
 * non-enumerating body — the user opens Advanced Settings to see each one).
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
export const combinedToastCopy = (deviceName: string): { title: string; message: string } => ({
  title: COMBINED_TITLE,
  message: `Camera "${deviceName}" needs several settings configured.`
});

/**
 * Payload describing the floating `<Notice>` the production
 * `dispatchFire` will post for a given `BucketFire`. Pure data — the
 * hook turns this into actual `post(...)` + `actions` JSX downstream.
 *
 * Extracted as a typed seam so the dispatcher tests can assert the
 * exact toast contents (id / title / message / severity / verb /
 * deep-link bucket) without needing React, jsdom, or a depot mock.
 *
 * Per `think-in-systems` (ai.knowledge/global): keep the payload pure;
 * navigation + JSX assembly happen on the React side.
 */
export interface BucketFireToastPayload {
  /** Composite id namespaced with a `:toast` suffix so re-invoking the
   * dispatcher for the same fire is idempotent (`post()` dedups by id).
   * Pattern: `${batchId}:${deviceId}:${bucket}:toast`. */
  notificationId: string;
  title: string;
  message: string;
  /** Always `'warning'` — drives the yellow severity bar.
   * Manual-close behavior (the Notice wrapper does not auto-close
   * `'warning'`). */
  severity: 'warning';
  /** Chip cascade verb sourced verbatim from `chipState.actionLink`
   * (e.g. "Authenticate", "Connect video"). Rendered as the visible
   * call-to-action label in the `<Notice>` actions element. */
  actionLinkLabel: string;
  /** `MultiAddSkipReason` consumed at click time by
   * `skipReasonToDefaultTab(deepLinkBucket)` → `IrisConnectionTab`,
   * then passed to `useSettings(device, { defaultPanel:
   * Panel.IrisConnection, defaultTab })`. Same dialog deep-link
   * channel the per-tile chip in `annotation.tsx` uses (see
   * `chip-click.ts::chipClickOptions`); the `useNavigate(state)`
   * pattern was a pre-monorepo design with no consumer in this
   * codebase (see `chip.tsx` file-level comment for the migration
   * rationale). */
  deepLinkBucket: MultiAddSkipReason;
}

/**
 * Build the floating-toast payload for a `BucketFire`. Pure function;
 * the React adapter wraps the output with `createElement` + `useNavigate`
 * to produce the `actions` element passed to `post(...)`.
 *
 * `actionLinkLabel` comes from `chipState.actionLink`, not a parallel
 * mapping.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
/**
 * The single source of truth for a bucket-fire toast id. Shape:
 * `${batchId}:${deviceId}:${key}:toast`, where `key` is a specific
 * `MultiAddSkipReason` bucket or the sentinel `'combined'`. Every consumer
 * that constructs a toast id (`buildBucketFireToastPayload`, the observer's
 * reconciliation identity, and the transitory-origin migration in
 * `migrate-transitory-origin.ts`) routes through this helper so the id shape
 * never drifts. Per `think-in-systems` (ai.knowledge/global).
 */
export const bucketToastId = (
  batchId: string,
  deviceId: string,
  key: MultiAddSkipReason | 'combined'
): string => `${batchId}:${deviceId}:${key}:toast`;

export const buildBucketFireToastPayload = (fire: BucketFire): BucketFireToastPayload => {
  if (fire.kind === 'combined') {
    // Combined alert (§2.3/§2.4): its own `:combined:toast` id (so it never
    // collides with a per-bucket specific), the product-confirmed combined
    // title/verb, and a deep-link to the auth-first routing bucket (§6.2).
    const { title, message } = combinedToastCopy(fire.deviceName);
    return {
      notificationId: bucketToastId(fire.batchId, fire.deviceId, 'combined'),
      title,
      message,
      severity: 'warning',
      actionLinkLabel: fire.actionLink,
      deepLinkBucket: fire.bucket
    };
  }
  // Single alert — unchanged shape (id / title / verb / deep-link).
  const { title, message } = bucketToToastCopy(fire.bucket, fire.deviceName);
  return {
    notificationId: bucketToastId(fire.batchId, fire.deviceId, fire.bucket),
    title,
    message,
    severity: 'warning',
    actionLinkLabel: fire.actionLink,
    deepLinkBucket: fire.bucket
  };
};

/**
 * Analytics `chip_type` value for a fire, per PRD §13 (ENG-4486 §2.6).
 * Combined → `'multiple'`; specific buckets map to their PRD names.
 * Pure function → tested directly.
 */
export type MultiAddChipType =
  | 'authentication'
  | 'configure_video'
  | 'configure_control'
  | 'select_model'
  | 'multiple';

export const fireToChipType = (fire: BucketFire): MultiAddChipType => {
  if (fire.kind === 'combined') return 'multiple';
  switch (fire.bucket) {
    case 'auth':
      return 'authentication';
    case 'video-failed':
      return 'configure_video';
    case 'control-failed':
      return 'configure_control';
    case 'missing-model':
      return 'select_model';
    default:
      // Unreachable: a single fire only ever carries one of the four buckets
      // above (`deriveMissingBuckets` produces no others). Degrade to the
      // combined bucket rather than emit an undefined property.
      return 'multiple';
  }
};

/**
 * The result of `evaluateDesiredFires` — three-way per-device classification
 * so the observer's reconciliation can tell CONFIRMED-RESOLVED from a
 * transient UNKNOWN (which must NOT clear a live alert — the bell-flicker
 * regression). A device is in EXACTLY one of:
 *   - `fires` — its lookup succeeded with a non-empty missing set (desired
 *     specific/combined alert);
 *   - `resolvedDeviceIds` — its lookup SUCCEEDED with an empty missing set
 *     (CONFIRMED resolved → auto-clear);
 *   - neither — its lookup returned `undefined` (UNKNOWN / broker-race /
 *     migration window → HOLD; the observer leaves the current alert as-is).
 */
export interface DesiredFires {
  fires: BucketFire[];
  resolvedDeviceIds: string[];
}

/**
 * Pure resolver: given the current origin slice and a chip lookup, classify
 * each origin device this tick (ENG-4486 declarative reconciliation). There is
 * NO dedup here: the observer reconciles against its per-device last-posted
 * identity and re-posts only on a change.
 *
 * Grouping when the lookup SUCCEEDS (per device, from `lookup.missing.length`
 * — the FULL set, not the single cascade pick):
 *   - `0` → `resolvedDeviceIds` (CONFIRMED resolved → observer auto-clears).
 *   - `1` → a SPECIFIC alert in `fires`.
 *   - `>=2` → a COMBINED alert in `fires` (PRD §9.2), routed auth-first (§6.2).
 * When the lookup returns `undefined` (UNKNOWN / transient), the device is in
 * NEITHER list — the observer HOLDS (does not clear), which is what fixes the
 * badge flicker while a camera is mid-add/commit.
 *
 * @param origins - Snapshot of `multiAddOriginByDeviceId`.
 * @param lookupChip - Per-device lookup returning chipState + missing set.
 * @returns `{ fires, resolvedDeviceIds }` (UNKNOWN devices in neither).
 */
export const evaluateDesiredFires = (
  origins: Record<string, MultiAddOrigin>,
  lookupChip: LookupOriginChipFn
): DesiredFires => {
  const fires: BucketFire[] = [];
  const resolvedDeviceIds: string[] = [];
  for (const [deviceId, origin] of Object.entries(origins)) {
    const lookup = lookupChip(deviceId);
    if (!lookup) {
      // UNKNOWN — broker has not surfaced this device yet (or it is mid
      // transitory→real migration). In NEITHER list → the observer HOLDS.
      continue;
    }
    const { chipState, missing, deviceSnapshot } = lookup;
    if (missing.length === 0) {
      // CONFIRMED RESOLVED — lookup succeeded, nothing missing → auto-clear.
      resolvedDeviceIds.push(deviceId);
      continue;
    }

    if (missing.length === 1) {
      // --- SPECIFIC alert (exactly one missing setting) -------------------
      const bucket = missing[0]!;
      // Verb comes verbatim from the chip cascade (chip verbs unchanged,
      // §2.7). For a single-missing device the cascade's top verdict IS this
      // bucket, so `chipState` is non-null; guard defensively so a
      // never-observed null can't post a verb-less toast.
      const actionLink = chipState?.actionLink;
      if (actionLink === undefined) continue;
      fires.push({
        kind: 'single',
        deviceId,
        bucket,
        buckets: [bucket],
        batchId: origin.batchId,
        deviceName: deviceSnapshot.deviceName,
        actionLink
      });
      continue;
    }

    // --- COMBINED alert (two or more missing settings) --------------------
    // Route by the shared `SETUP_BUCKET_ORDER` (device/setup-order.ts) — the
    // same order the tile chip uses. Auth is first (§6.2: other sections are
    // locked until auth succeeds — PRD §10), and Driver precedes Control so a
    // generic camera is not sent to Control before its driver is chosen (QA
    // finding 8). Ordering here rather than trusting `missing[0]` keeps the
    // route correct even for a lookup that returns an unordered set.
    // Per `think-in-systems` (ai.knowledge/global).
    const routingBucket = firstInSetupOrder(missing) ?? missing[0]!;
    fires.push({
      kind: 'combined',
      deviceId,
      bucket: routingBucket,
      buckets: missing,
      batchId: origin.batchId,
      deviceName: deviceSnapshot.deviceName,
      // Combined has no single chip verb — use the neutral combined verb.
      actionLink: COMBINED_ACTION_LINK
    });
  }
  return { fires, resolvedDeviceIds };
};
